/**
 * Deck phase (internally still 'shipped'). The final artefact:
 * an interactive slide deck of the plan.
 *
 * Owner controls:
 *   - "Generate public link" → mints board.deckShareToken
 *   - Toggle to revoke
 *   - "Open deck" → preview the deck in this tab
 * Members:
 *   - See preview, can open the deck
 */

import { useEffect, useState } from 'react';
import { api } from '../api';
import { UiNode } from '../lib/UiNode';
import { IconCopy, IconCheck, IconEye, IconClose, IconArrowRight } from '../components/Icon';
import type { BoardDetail, Cluster, PlanState } from '../../shared/types';
import { Link } from '../router';

export function DeckPhase({
  board,
  clusters,
  planState,
  onBoardChange,
}: {
  board: BoardDetail;
  clusters: Cluster[];
  planState: PlanState;
  onBoardChange: (next: BoardDetail) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const isOwner = board.role === 'owner';

  const deckUrl = board.deckShareToken
    ? `${window.location.origin}/d/${board.deckShareToken}`
    : null;

  async function mintToken() {
    if (!isOwner || busy) return;
    setBusy(true);
    try {
      const r = await api.shareDeck(board.id);
      onBoardChange({ ...board, deckShareToken: r.token });
    } finally {
      setBusy(false);
    }
  }

  async function revoke() {
    if (!isOwner || busy) return;
    if (!confirm('Revoke the public deck link? Anyone with the old URL will no longer be able to view.')) return;
    setBusy(true);
    try {
      await api.revokeDeck(board.id);
      onBoardChange({ ...board, deckShareToken: null });
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    if (!deckUrl) return;
    await navigator.clipboard.writeText(deckUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div className="flex flex-col gap-4">
      <section className="panel p-4">
        <h2 className="text-base">Share publicly</h2>
        <p className="mt-1 text-sm text-cream-3">
          Mint a public link to share an interactive deck of the plan.
          Anyone with the link can view; no sign-in needed.
        </p>

        {!deckUrl && isOwner && (
          <UiNode
            as="button"
            name="deck-mint-button"
            entity="deck"
            action="mint"
            state={busy ? 'busy' : 'idle'}
            className="btn btn-primary mt-3"
            onClick={mintToken}
            disabled={busy}
          >
            {busy ? 'Minting…' : 'Get public link'}
          </UiNode>
        )}

        {!deckUrl && !isOwner && (
          <p className="mt-2 text-xs text-cream-3">Ask the board owner to mint a public link.</p>
        )}

        {deckUrl && (
          <div className="mt-3 flex flex-col gap-2">
            <div className="flex items-stretch gap-2">
              <input
                className="input flex-1 font-mono text-xs"
                readOnly
                value={deckUrl}
                onFocus={(e) => e.currentTarget.select()}
              />
              <button type="button" className="btn btn-ghost inline-flex items-center gap-1.5" onClick={copy}>
                {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <div className="flex gap-2">
              <Link to={`/d/${board.deckShareToken}`} className="btn btn-primary inline-flex items-center gap-1.5">
                <IconEye size={14} />
                Open deck
              </Link>
              {isOwner && (
                <button type="button" className="btn btn-ghost inline-flex items-center gap-1.5 text-rust" onClick={revoke} disabled={busy}>
                  <IconClose size={14} />
                  Revoke
                </button>
              )}
            </div>
          </div>
        )}
      </section>

      <section className="panel p-4">
        <h2 className="text-base">Preview</h2>
        <p className="mt-1 text-sm text-cream-3">A taste of what the deck looks like.</p>
        <div className="mt-3">
          <DeckPreview planState={planState} clusters={clusters} board={board} />
        </div>
      </section>
    </div>
  );
}

function DeckPreview({
  board,
  planState,
  clusters,
}: {
  board: BoardDetail;
  planState: PlanState;
  clusters: Cluster[];
}) {
  const [slide, setSlide] = useState(0);

  // Slide 0 = title; then one per day; final = closing.
  const days = Array.from(
    new Set(planState.sessions.map((s) => s.date).sort()),
  );
  const total = 1 + days.length + 1;

  useEffect(() => {
    if (slide >= total) setSlide(0);
  }, [slide, total]);

  return (
    <div className="flex flex-col gap-2">
      <div
        className="aspect-video w-full overflow-hidden rounded-xl"
        style={{
          background: `linear-gradient(135deg, hsl(28,70%,18%) 0%, hsl(15,60%,12%) 100%)`,
        }}
      >
        <SlideContent
          board={board}
          planState={planState}
          clusters={clusters}
          days={days}
          slide={slide}
          total={total}
        />
      </div>
      <div className="flex items-center justify-between">
        <button
          type="button"
          className="btn btn-ghost"
          onClick={() => setSlide((s) => Math.max(0, s - 1))}
          disabled={slide === 0}
        >
          ←
        </button>
        <span className="text-xs text-cream-3">
          Slide {slide + 1} / {total}
        </span>
        <button
          type="button"
          className="btn btn-ghost inline-flex items-center gap-1"
          onClick={() => setSlide((s) => Math.min(total - 1, s + 1))}
          disabled={slide === total - 1}
        >
          →
        </button>
      </div>
    </div>
  );
}

function SlideContent({
  board,
  planState,
  clusters,
  days,
  slide,
  total,
}: {
  board: BoardDetail;
  planState: PlanState;
  clusters: Cluster[];
  days: string[];
  slide: number;
  total: number;
}) {
  if (slide === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 p-8 text-center">
        <h1 className="text-3xl text-cream-1 sm:text-4xl">{board.name}</h1>
        {board.tagline && <p className="text-base text-cream-2">{board.tagline}</p>}
        {(board.startsOn || board.endsOn) && (
          <p className="mt-2 text-sm text-cream-3">
            {board.startsOn} — {board.endsOn}
          </p>
        )}
      </div>
    );
  }
  if (slide === total - 1) {
    const ownerNames = Array.from(
      new Set(
        planState.sessions
          .map((s) => board.members.find((m) => m.id === s.ownerId)?.name)
          .filter(Boolean) as string[],
      ),
    );
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
        <h2 className="text-xl text-cream-1">Let's make it happen</h2>
        {ownerNames.length > 0 && (
          <p className="text-sm text-cream-3">
            Owners: {ownerNames.join(' · ')}
          </p>
        )}
        <IconArrowRight size={20} />
      </div>
    );
  }
  const date = days[slide - 1]!;
  const dayLabel = new Date(date + 'T00:00:00').toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
  const dayCluster = clusters; // available for future themed slide backgrounds
  void dayCluster;
  const sessions = planState.sessions
    .filter((s) => s.date === date && s.isRevealed)
    .sort((a, b) => a.startMinute - b.startMinute);
  return (
    <div className="flex h-full flex-col gap-3 p-6">
      <header>
        <p className="text-xs uppercase tracking-wide text-cream-3">{dayLabel}</p>
      </header>
      <ul className="flex flex-col gap-2 overflow-hidden">
        {sessions.map((s) => {
          const owner = board.members.find((m) => m.id === s.ownerId);
          return (
            <li
              key={s.id}
              className="rounded-md border border-cream-4/30 bg-black/30 p-2 text-sm text-cream-1"
              style={{ borderLeft: `3px solid hsl(${s.colorHue}, 70%, 55%)` }}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium">{s.name}</span>
                <span className="text-xs text-cream-3">
                  {fmt(s.startMinute)} – {fmt(s.endMinute)}
                </span>
              </div>
              {owner && <span className="text-xs text-cream-3">{owner.name}</span>}
            </li>
          );
        })}
        {sessions.length === 0 && (
          <li className="text-sm text-cream-3">Nothing scheduled.</li>
        )}
      </ul>
    </div>
  );
}

function fmt(m: number): string {
  const h = Math.floor(m / 60).toString().padStart(2, '0');
  const mm = (m % 60).toString().padStart(2, '0');
  return `${h}:${mm}`;
}
